import {
  Badge,
  Container,
  EmptyState,
  Flex,
  Heading,
  Link,
  Table,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { ProviderClients, ProviderClientTypes } from "@/client"
import PendingProviderClients from "@/components/ProviderClients/PendingProviderClients"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"

const clientsSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 10

function getClientsQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      ProviderClients.readProviderClientsByUserId({
        query: { skip: (page - 1) * PER_PAGE, limit: PER_PAGE },
      }),
    queryKey: ["clients", { page }],
  }
}

export const Route = createFileRoute("/_layout/clients")({
  component: Clients,
  validateSearch: (search) => clientsSearchSchema.parse(search),
})

function ClientsTable() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page } = Route.useSearch()

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getClientsQueryOptions({ page }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page: nextPage }),
    })

  const clients = data?.data ?? []
  const count = data?.count ?? 0

  // Derive the provider from the loaded clients — don't rely on sessionStorage
  // which is only set after a client is clicked.
  const providerId = clients[0]?.providerId ?? sessionStorage.getItem("selectedClientProviderId") ?? ""
  const { data: clientTypesData } = useQuery({
    queryKey: ["providerClientTypes", providerId],
    queryFn: () => ProviderClientTypes.listProviderClientTypes({ query: { providerId } }),
    enabled: !!providerId,
  })
  const clientTypeMap = new Map((clientTypesData?.data ?? []).map((t) => [t.id, t.name]))

  const setSelectedClient = (clientId: string, clientName: string, providerClientId: string) => {
    sessionStorage.setItem("selectedClientId", clientId)
    sessionStorage.setItem("selectedClientName", clientName)
    sessionStorage.setItem("selectedClientProviderId", providerClientId)
    navigate({ to: "/client" })
  }

  if (isLoading) {
    return <PendingProviderClients />
  }

  if (clients.length === 0) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No clients aligned to your skillset</EmptyState.Title>
            <EmptyState.Description>
              Clients are shown based on your assigned user type and skills. Contact your administrator if you believe this is incorrect.
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <>
      <Table.Root size={{ base: "sm", md: "md" }}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader w="sm">Client Id</Table.ColumnHeader>
            <Table.ColumnHeader w="sm">Full Name</Table.ColumnHeader>
            <Table.ColumnHeader w="sm">Email</Table.ColumnHeader>
            <Table.ColumnHeader w="sm">Client Type</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {clients.map((client) => {
            const preferName = client.prefername || client.firstname || ""
            const fullName = [preferName, client.lastname].filter(Boolean).join(" ") || "N/A"
            return (
              <Table.Row key={client.id} opacity={isPlaceholderData ? 0.5 : 1}>
                <Table.Cell truncate maxW="sm">
                  <Link
                    color="blue.600"
                    textDecoration="underline"
                    cursor="pointer"
                    onClick={() => setSelectedClient(client.id, fullName, client.providerId)}
                  >
                    {client.providerClientId}
                  </Link>
                </Table.Cell>
                <Table.Cell color={fullName === "N/A" ? "gray" : "inherit"} truncate maxW="30%">
                  {fullName}
                </Table.Cell>
                <Table.Cell color={!client.email ? "gray" : "inherit"} truncate maxW="30%">
                  {client.email || "N/A"}
                </Table.Cell>
                <Table.Cell>
                  {client.providerClientTypeId ? (
                    <Badge colorPalette="purple" size="sm">
                      {clientTypeMap.get(client.providerClientTypeId) ?? "—"}
                    </Badge>
                  ) : (
                    <span style={{ color: "#A0AEC0" }}>—</span>
                  )}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page: nextPage }) => setPage(nextPage)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function Clients() {
  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Clients
      </Heading>
      <ClientsTable />
    </Container>
  )
}
